/**
 * Parses a typed time into minutes past midnight, or null if it isn't a time yet.
 *
 * Accepts what people actually type: "8:01", "8:01 am", "8:01PM", "20:01", "8", "8pm".
 * The picker's list only offers quarter hours, so this is the path to any other minute.
 *
 * When no am/pm is given and the hour is 1–11, the reference time's meridiem is kept — so
 * with the field reading 3:00 PM, typing "8:01" means 8:01 PM. Hours 0 and 12–23 name
 * themselves and are read as 24-hour, so a bare "12" is noon.
 */
export function parseTimeInput(
  text: string,
  referenceMinutes: number
): number | null {
  const match = text
    .trim()
    .toLowerCase()
    .match(/^(\d{1,2})(?::(\d{1,2}))?\s*([ap])\.?m?\.?$|^(\d{1,2})(?::(\d{1,2}))?$/);
  if (!match) return null;

  const hours = Number(match[1] ?? match[4]);
  const minutesText = match[2] ?? match[5];
  const minutes = minutesText === undefined ? 0 : Number(minutesText);
  const meridiem = match[3];

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  if (minutes > 59) return null;
  // A single "8:0" is still mid-typing, not 8:00 — wait for the second digit.
  if (minutesText !== undefined && minutesText.length !== 2) return null;

  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    const base = hours % 12;
    return (meridiem === "p" ? base + 12 : base) * 60 + minutes;
  }

  if (hours > 23) return null;
  // Read anything that isn't 1–11 as 24-hour time: 0 is midnight, and 12 through 23 all
  // name themselves. That includes a bare "12" — which is noon, not midnight, and reading
  // it as midnight from a morning field would have silently put the post in the past.
  if (hours === 0 || hours >= 12) return hours * 60 + minutes;

  // 1–11 genuinely are ambiguous, so keep whichever half of the day the field is in.
  const referenceIsPm = referenceMinutes >= 12 * 60;
  return (referenceIsPm ? hours + 12 : hours) * 60 + minutes;
}
